import React, { useState, useEffect, useRef, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/DeliveryUpdate.css";

/* UI status -> status accepted by drs.DeliveryUpdate / drs.DRSConsignment (see drs_Appentry_test) */
const STATUS_TO_DB = {
  DELIVERED: "DELIVERED",
  PARTIAL: "PARTIAL",
  OUT_FOR_DELIVERY: "ATTEMPTED",
  UNDELIVERED: "ATTEMPTED",
  REFUSED: "ATTEMPTED",
  RTO: "RETURNED",
};


/* ============================================================
   CONFIG — override via props
   ============================================================ */
const DEFAULT_STATUSES = [
  { value: "DELIVERED", label: "Delivered" },
  { value: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
  { value: "UNDELIVERED", label: "Undelivered" },
  { value: "PARTIAL", label: "Partially Delivered" },
  { value: "REFUSED", label: "Refused by Consignee" },
  { value: "RTO", label: "Return to Origin" },
];
const DEFAULT_REASONS = [
  "Consignee not available",
  "Premises closed / Door locked",
  "Incorrect / incomplete address",
  "Consignee refused to accept",
  "Payment / documents not ready",
  "Vehicle breakdown",
  "Other",
];
const DEFAULT_RECEIVED_AT = [
  "Main Gate / Security",
  "Stores / Warehouse",
  "Reception",
  "Site Office",
  "Consignee Premises",
];
const MAX_BYTES = 5 * 1024 * 1024;
// Same allow-list as MobileVersionController's AllowedPodExtensions — reject
// early here instead of after a full upload just to get a 400 back.
const ALLOWED_POD_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const EMPTY_FORM = {
  dest: "",
  time: "",
  status: "",
  receiver: "",
  receivedAt: "",
  mobile: "",
  pkgs: "",
  reason: "",
  remarks: "",
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function nowLocal() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
// Date part is always "today" — the delivery-time picker below never lets the
// user choose a different day, only a time on today's date.
const todayISO = () => nowLocal().slice(0, 10);
// Just the "HH:mm" portion of a "YYYY-MM-DDTHH:mm" value (for the time input).
const timeOnly = (dt) => (dt && dt.includes("T") ? dt.slice(11, 16) : "");
// Current clock time as "HH:mm" — used as the time input's max, recomputed
// fresh on every render so it stays accurate as the clock moves.
const nowTimeOnly = () => nowLocal().slice(11, 16);

/* Which fields each status needs */
function rulesFor(status) {
  const receipt = status === "DELIVERED" || status === "PARTIAL";
  return {
    receiptRequired: receipt, // Receiver + Received At mandatory
    showMobile: receipt,
    showPod: false, // POD photo hidden everywhere — not needed, not required, never uploaded
    showPkgs: status === "PARTIAL",
    showReason: ["UNDELIVERED", "REFUSED", "RTO", "PARTIAL"].includes(status),
  };
}

function badgeClass(status = "") {
  const s = status.toUpperCase();
  if (s === "DELIVERED") return "green";
  if (["IN TRANSIT", "OUT FOR DELIVERY", "PARTIALLY DELIVERED"].includes(s)) return "amber";
  if (["UNDELIVERED", "RTO", "RETURN TO ORIGIN", "REFUSED BY CONSIGNEE"].includes(s)) return "red";
  return "";
}

/* ============================================================
   Icons
   ============================================================ */
const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="20" y1="12" x2="4" y2="12" />
    <polyline points="10 6 4 12 10 18" />
  </svg>
);
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.5" y2="16.5" />
  </svg>
);
const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const CameraIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

/* ============================================================
   Toast hook
   ============================================================ */
function useToast() {
  const [toast, setToast] = useState({ msg: "", type: "", show: false });
  const timer = useRef();
  const show = useCallback((msg, type = "") => {
    clearTimeout(timer.current);
    setToast({ msg, type, show: true });
    timer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2600);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, show];
}

/* ============================================================
   Small building blocks
   ============================================================ */
function Field({ name, label, required, error, full, fieldRef, children }) {
  return (
    <div ref={fieldRef} className={`dlv-field ${full ? "full" : ""} ${error ? "has-error" : ""}`} data-field={name}>
      <label htmlFor={`dlv-${name}`}>
        {label} {required && <span className="dlv-req">*</span>}
      </label>
      {children}
      {error && <div className="dlv-err">{error}</div>}
    </div>
  );
}

function Detail({ label, value, badge }) {
  return (
    <div className="dlv-detail">
      <dt>{label}</dt>
      {value ? (
        <dd>{badge ? <span className={`dlv-badge ${badgeClass(value)}`}>{value}</span> : value}</dd>
      ) : (
        <dd className="empty">----------</dd>
      )}
    </div>
  );
}

/* Custom "Received At" autocomplete — replaces the native <input list>
   datalist (which renders as the browser's own unstyled "Saved data"
   popup). Type to filter, click or arrow-key + Enter to pick; free text is
   still allowed since the field isn't restricted to the listed options. */
function ReceivedAtAutocomplete({ id, value, onChange, options, placeholder, invalid }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  const filtered = options.filter((o) => o.toLowerCase().includes((value || "").toLowerCase()));

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = (opt) => {
    onChange(opt);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0 && filtered[highlight]) {
        e.preventDefault();
        choose(filtered[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        id={id}
        type="text"
        className={`dlv-input ${invalid ? "invalid" : ""}`}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <ul
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            margin: 0,
            padding: "6px 0",
            listStyle: "none",
            background: "#fff",
            border: "1px solid #d7dee3",
            borderRadius: 8,
            boxShadow: "0 8px 20px rgba(15, 46, 60, 0.14)",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {filtered.map((opt, i) => (
            <li
              key={opt}
              onMouseDown={(e) => {
                e.preventDefault(); // keep input focus so the click isn't lost to blur first
                choose(opt);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "9px 14px",
                fontSize: 14,
                cursor: "pointer",
                background: i === highlight ? "#eaf3f6" : "transparent",
                color: "#1f3a44",
              }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================

   Main screen
   ============================================================ */
export default function DeliveryUpdate({
  statuses = DEFAULT_STATUSES,
  reasons = DEFAULT_REASONS,
  receivedAtOptions = DEFAULT_RECEIVED_AT,
  companyCode = "", // company_code sent to drs-delivery-update (drs_Appentry_test)
  userId = "",      // guser_id sent to drs-delivery-update
  demo = false, // API is wired — pass demo={true} to force mock data
  onBack = () => window.history.back(),
  onUpdated, // (consignmentNo, statusLabel, response) => void
}) {
  const [cnNo, setCnNo] = useState("");
  const [cnInvalid, setCnInvalid] = useState(false);
  const [tracked, setTracked] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [pod, setPod] = useState(null); // { file, url }
  const [submitting, setSubmitting] = useState(false);
  // Status dropdown options come from the backend (drs.DeliveryStatusMaster).
  // The `statuses` prop / DEFAULT_STATUSES is only the fallback if that call fails.
  const [statusOptions, setStatusOptions] = useState(statuses);
  useEffect(() => {
    if (demo) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/drs-statuses");
        const rows = res.data?.data;
        if (!cancelled && res.data?.success && Array.isArray(rows) && rows.length) setStatusOptions(rows);
      } catch {
        // keep the fallback list
      }
    })();
    return () => { cancelled = true; };
  }, [demo]);
  const statusRow = (value) => statusOptions.find((s) => s.value === value);
  // DB status (DELIVERED / PARTIAL / ATTEMPTED / RETURNED) for the chosen option
  const dbStatusOf = (value) => statusRow(value)?.dbStatus || STATUS_TO_DB[value] || "ATTEMPTED";
  // OTP step (same flow as DRS Updation): shown on submit for DELIVERED / PARTIAL
  // when the consignment has Otp_Required = 1. Update is sent only after the OTP verifies.
  const [otp, setOtp] = useState({ open: false, otpId: null, mobile: "", code: "", busy: false, error: "", cooldown: 0 });
  useEffect(() => {
    if (!otp.open || otp.cooldown <= 0) return;
    const id = setTimeout(() => setOtp((o) => ({ ...o, cooldown: o.cooldown - 1 })), 1000);
    return () => clearTimeout(id);
  }, [otp.open, otp.cooldown]);
  const [toast, notify] = useToast();
  // Ticks once a second purely to force a re-render, so every place that
  // calls nowLocal()/todayISO()/nowTimeOnly() during render (the Date box,
  // the Time picker's max, the future-time clamp) is comparing against the
  // real current second instead of whatever time it was at the last
  // unrelated re-render (e.g. the last keystroke).
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const podInputRef = useRef(null);
  const fieldRefs = useRef({});
  const regField = (name) => (el) => (fieldRefs.current[name] = el);

  const rules = rulesFor(form.status);
  // Form is locked either when it's already delivered, or when this
  // consignment was never assigned to a DRS run in the first place — either
  // way there's nothing to update here.
  // DRS-run check disabled: only "already delivered" locks the form now.
  // const notUpdatable = tracked?.alreadyDelivered || tracked?.canUpdate === false;
  const notUpdatable = !!tracked?.alreadyDelivered;

  useEffect(() => () => pod && URL.revokeObjectURL(pod.url), [pod]);

  /* ----- Form helpers ----- */
  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };
  const bind = (name) => ({
    id: `dlv-${name}`,
    value: form[name],
    onChange: (e) => setField(name, e.target.value),
    className: `dlv-input ${errors[name] ? "invalid" : ""}`,
  });

  const handleStatus = (e) => {
    const status = e.target.value;
    setForm((f) => ({ ...f, status }));
    setErrors((er) => ({ ...er, status: undefined, receiver: undefined, receivedAt: undefined, pkgs: undefined, reason: undefined }));
    if (!rulesFor(status).showPod) clearPod();
  };

  /* ----- Track ----- */
  const fetchConsignment = async (cn) => {
    if (demo) {
      await sleep(600);
      if (cn.length < 4) throw new Error("Consignment not found");
      return {
        cnNo: cn,
        consignor: "Shree Transformers Pvt. Ltd., Kalyan",
        consignee: "Gujarat Power Solutions, Vadodara",
        status: "In Transit",
        alreadyDelivered: false,
        canUpdate: true,
        receivedBy: null,
        remarks: null,
        deliveredOn: null,
        podImageUrl: null,
        date: new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
        destination: "Vadodara",
      };
    }
    const res = await api.get(`/drs-track/${encodeURIComponent(cn)}`);
    if (!res.data?.success) throw new Error(res.data?.message || "Consignment not found");
    return res.data.data;
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    const cn = cnNo.trim().toUpperCase();
    if (!cn) {
      setCnInvalid(true);
      notify("Enter consignment number", "bad");
      return;
    }
    setTracking(true);
    try {
      const data = await fetchConsignment(cn);
      setTracked(data);
      // Default Delivery Dest. and Time as soon as a consignment is tracked,
      // so Time is never left blank — it starts at "now" and the existing
      // max={nowLocal()} + validate() check still stop it being pushed forward.
      setForm((f) => ({
        ...f,
        dest: f.dest || data.destination || f.dest,
        time: f.time || nowLocal(),
      }));

      // Read back what was saved on the LAST update, for ANY status (not only
      // Delivered): status, receiver, received at, mobile and remarks.
      const savedStatus =
        statusOptions.find((s) => s.id != null && s.id === data.statusId)?.value ||
        statusOptions.find((s) => (s.dbStatus || STATUS_TO_DB[s.value]) === data.dbStatus)?.value ||
        "";
      const hasSaved = !!(savedStatus || data.receivedBy || data.receivedAt || data.receiverMobile || data.remarks);

      if (data.alreadyDelivered) {
        // Delivered: show the real record in the (locked) form, including its time.
        setForm((f) => ({
          ...f,
          status: savedStatus || "DELIVERED",
          receiver: data.receivedBy || "",
          receivedAt: data.receivedAt || "",
          mobile: data.receiverMobile || "",
          remarks: data.remarks || "",
          time: data.deliveredOn || f.time,
        }));

        if (data.podImageUrl) {
          try {
            const imgRes = await api.get(data.podImageUrl, { responseType: "blob" });
            setPod({ file: null, url: URL.createObjectURL(imgRes.data) });
          } catch {
            // No POD photo to show — not fatal, the rest of the readback still stands.
          }
        }

        notify("This consignment is already delivered — no further update needed.", "bad");
      // DRS-run check disabled — update allowed regardless of DeliveryMode.
      // } else if (data.canUpdate === false) {
      //   // Not on a DRS delivery run (drs.DeliveryUpdate has no DeliveryMode = 'DRS'
      //   // row for it) — nothing to update from this screen.
      //   notify("This consignment is not on a DRS delivery run — no update needed.", "bad");
      } else {
        // Not delivered yet (Attempted / Partial / Undelivered / Out for Delivery ...):
        // pre-fill the form from the last update; Time stays "now" for the new update.
        if (hasSaved) {
          setForm((f) => ({
            ...f,
            status: savedStatus || f.status,
            receiver: data.receivedBy || "",
            receivedAt: data.receivedAt || "",
            mobile: data.receiverMobile || "",
            remarks: data.remarks || "",
          }));
        }
        notify("Consignment found", "ok");
      }
    } catch (err) {
      setTracked(null);
      notify(err.response?.data?.message || err.message || "Unable to fetch consignment", "bad");
    } finally {
      setTracking(false);
    }
  };

  /* ----- POD photo ----- */
  const clearPod = () => {
    setPod(null);
    if (podInputRef.current) podInputRef.current.value = "";
  };
  const handlePod = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_POD_TYPES.includes(f.type)) {
      notify("Please select a JPG, PNG or WEBP image", "bad");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      notify("Image must be under 5 MB", "bad");
      e.target.value = "";
      return;
    }
    setPod({ file: f, url: URL.createObjectURL(f) });
  };

  /* ----- Validation ----- */
  const validate = () => {
    const er = {};
    if (!form.dest.trim()) er.dest = "Delivery destination is required";
    if (!form.time) er.time = "Delivery time is required";
    else if (form.time > nowLocal()) er.time = "Time cannot be in the future";
    if (!form.status) er.status = "Please select a status";
    if (rules.receiptRequired && !form.receiver.trim()) er.receiver = "Receiver name is required";
    if (rules.receiptRequired && !form.receivedAt.trim()) er.receivedAt = "Received at is required";
    if (rules.showMobile && form.mobile && !/^[6-9]\d{9}$/.test(form.mobile))
      er.mobile = "Enter a valid 10-digit mobile number";
    if (rules.showPkgs && !(Number(form.pkgs) > 0)) er.pkgs = "Enter packages delivered";
    if (rules.showReason && !form.reason) er.reason = "Please select a reason";
    // Backend requires a POD photo for DELIVERED / PARTIAL — enforce it here too
    // so the driver isn't sent to the server before finding out it's missing.
    if (rules.showPod && !pod) er.pod = "Please capture or upload a delivery photo";

    setErrors(er);
    const first = Object.keys(er)[0];
    if (first) fieldRefs.current[first]?.scrollIntoView({ behavior: "smooth", block: "center" });
    return !first;
  };

  /* ----- Submit ----- */
  const resetForm = (all) => {
    setForm({ ...EMPTY_FORM, time: nowLocal() }); // keep Time defaulted to current date/time after a reset
    setErrors({});
    clearPod();
    if (all) {
      setCnNo("");
      setCnInvalid(false);
      setTracked(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tracked) {
      notify("Track a consignment first", "bad");
      return;
    }
    if (tracked.alreadyDelivered) {
      notify("This consignment is already delivered — no further update needed.", "bad");
      return;
    }
    // DRS-run check disabled — update allowed regardless of DeliveryMode.
    // if (tracked.canUpdate === false) {
    //   notify("This consignment is not on a DRS delivery run — no update needed.", "bad");
    //   return;
    // }
    if (!validate()) {
      notify("Please fill the required fields", "bad");
      return;
    }

    // DELIVERED / PARTIAL on an Otp_Required consignment: OTP must be entered
    // and verified BEFORE the update is sent. Everything else submits directly.
    const dbStatus = dbStatusOf(form.status);
    const needsOtp = !demo && tracked.otpRequired && (dbStatus === "DELIVERED" || dbStatus === "PARTIAL");
    if (needsOtp) {
      await startOtp();
      return;
    }
    await submitUpdate(null);
  };

  /* ----- OTP ----- */
  const startOtp = async () => {
    setSubmitting(true);
    try {
      // An OTP may already have been sent (e.g. at DRS preparation) — reuse it.
      const st = await api.get("/drs-otp-status", { params: { consignmentNo: tracked.cnNo } });
      if (st.data?.exists) {
        setOtp({ open: true, otpId: st.data.otpId, mobile: st.data.mobileMasked || "", code: "", busy: false, error: "", cooldown: 0 });
        return;
      }
      await sendOtp(false);
    } catch (err) {
      notify(err.response?.data?.message || err.message || "Unable to send OTP", "bad");
    } finally {
      setSubmitting(false);
    }
  };

  const sendOtp = async (isResend) => {
    setOtp((o) => ({ ...o, busy: true, error: "" }));
    try {
      const res = await api.post("/drs-send-otp", {
        consignmentNo: tracked.cnNo,
        deliveryBoyId: Number(userId) || 0,
      });
      if (!res.data?.success) throw new Error(res.data?.message || "Unable to send OTP");
      setOtp({ open: true, otpId: res.data.otpId, mobile: res.data.mobileMasked || "", code: "", busy: false, error: "", cooldown: 60 });
      notify(isResend ? "OTP resent" : "OTP sent to consignee", "ok");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Unable to send OTP";
      setOtp((o) => ({ ...o, busy: false, error: o.open ? msg : "" }));
      notify(msg, "bad");
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{4}$/.test(otp.code)) {
      setOtp((o) => ({ ...o, error: "Enter the 4-digit OTP" }));
      return;
    }
    setOtp((o) => ({ ...o, busy: true, error: "" }));
    try {
      const res = await api.post("/drs-verify-otp", { otpId: otp.otpId, otp: otp.code });
      if (!res.data?.success) {
        setOtp((o) => ({ ...o, busy: false, error: res.data?.message || "Incorrect OTP" }));
        return;
      }
      const verifiedId = otp.otpId;
      setOtp({ open: false, otpId: null, mobile: "", code: "", busy: false, error: "", cooldown: 0 });
      await submitUpdate(verifiedId); // OTP is correct -> now submit the update
    } catch (err) {
      setOtp((o) => ({ ...o, busy: false, error: err.response?.data?.message || err.message || "Verification failed" }));
    }
  };

  const closeOtp = () => setOtp({ open: false, otpId: null, mobile: "", code: "", busy: false, error: "", cooldown: 0 });

  const submitUpdate = async (verifiedOtpId) => {
    setSubmitting(true);
    try {
      let response;
      if (demo) {
        await sleep(800);
      } else {
        const [datePart, rawTime = "00:00"] = form.time.split("T");
        const timePart = rawTime.length === 5 ? `${rawTime}:00` : rawTime;
        const remarkText = [rules.showReason ? form.reason : "", form.remarks.trim()].filter(Boolean).join(" - ");

        // Same field names drs-delivery-update expects (InsertAppRequest), now sent as
        // multipart/form-data instead of JSON so the POD photo can travel in the same
        // request — a file can't go inside a JSON body without base64-encoding it.
        const fd = new FormData();
        fd.append("cons_no", tracked.cnNo);
        fd.append("consignee_name", tracked.consignee || "");
        fd.append("cons_delivery_date", datePart);
        fd.append("drs_time", `${datePart} ${timePart}`);
        fd.append("cons_status", dbStatusOf(form.status));
        fd.append("received_by", form.receiver.trim());
        fd.append("received_at", form.receivedAt.trim()); // location picked in "Received At" (saved as-is, not a date/time)
        fd.append("receiver_mobile", rules.showMobile ? form.mobile : "");
        fd.append("remarks", remarkText);
        fd.append("delivery_place", form.dest.trim());
        fd.append("booking_place", tracked.bookingPlace || "");
        fd.append("destination", form.dest.trim());
        fd.append("cons_status_id", String(statusRow(form.status)?.id ?? 0)); // master StatusId, stored with the update
        fd.append("guser_id", String(userId ?? ""));
        fd.append("company_code", String(tracked.companyCode ?? companyCode ?? ""));
        if (verifiedOtpId) fd.append("otp_id", String(verifiedOtpId)); // server re-checks it
        fd.append("longitude", "0");
        fd.append("latitude", "0");
        // Field name must be "PodImage" to match InsertAppRequest.PodImage on the server.
        if (rules.showPod && pod?.file) fd.append("PodImage", pod.file, pod.file.name);

        // IMPORTANT: don't let the shared axios instance's default
        // 'Content-Type: application/json' (or similar) header ride along on
        // this request. FormData needs the browser to set
        // 'multipart/form-data; boundary=...' itself — if a JSON content type
        // (or a fixed multipart type with no boundary) is forced instead, the
        // server can't parse any of the form fields, and every [FromForm]
        // property (cons_no, destination, time, status, ...) silently binds
        // to its default/empty value. That's what was producing
        // "Consignment number is required" even though cons_no was set here.
        const res = await api.post("/drs-delivery-update", fd, {
          headers: { "Content-Type": undefined },
        });
        response = res.data;
        if (!response?.success) {
          if (response?.code === 8) {
            // Someone else delivered it between this device's track and submit —
            // sync the UI so it locks instead of inviting another retry.
            setTracked((t) => (t ? { ...t, alreadyDelivered: true, status: "Delivered" } : t));
          }
          throw new Error(response?.body || response?.message || "Update failed");
        }
      }
      const label = statusRow(form.status)?.label || form.status;
      notify(`Consignment ${tracked.cnNo} updated: ${label}`, "ok");
      onUpdated?.(tracked.cnNo, label, response);
      // Clear everything after a successful update — consignment number, tracked
      // details and all fields — with Time reset to the current date/time.
      resetForm(true);
    } catch (err) {
      notify(err.response?.data?.body || err.response?.data?.message || err.message || "Update failed, try again", "bad");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dlv-root">
      <header className="dlv-appbar">
        <button type="button" aria-label="Back" onClick={onBack}>
          <BackIcon />
        </button>
        <h1>Delivery</h1>
      </header>

      <main className="dlv-main">
        {/* ===== Track consignment ===== */}
        <section className="dlv-card">
          <form className="dlv-track-row" onSubmit={handleTrack} autoComplete="off">
            <input
              className={`dlv-input ${cnInvalid ? "invalid" : ""}`}
              type="text"
              value={cnNo}
              onChange={(e) => {
                setCnNo(e.target.value.toUpperCase());
                setCnInvalid(false);
              }}
              placeholder="Consignment No."
              aria-label="Consignment number"
              autoCapitalize="characters"
            />
            <button className="dlv-btn dlv-btn-primary" type="submit" disabled={tracking}>
              {tracking ? <span className="dlv-spinner" /> : (<><SearchIcon /><span>Track</span></>)}
            </button>
          </form>

          <dl className="dlv-details">
            <Detail label="Consignor" value={tracked?.consignor} />
            <Detail label="Consignee" value={tracked?.consignee} />
            <Detail label="Con. Status" value={tracked?.status} badge />
            <Detail label="Date" value={tracked?.date} />
          </dl>

          {tracked?.alreadyDelivered && (
            <div
              className="dlv-delivered-banner"
              role="status"
              style={{
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: "#e8f6ed",
                color: "#1e7a3d",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              ✓ This consignment is already delivered. No further update is needed.
            </div>
          )}

          {/* DRS-run banner disabled — update allowed regardless of DeliveryMode
          {!tracked?.alreadyDelivered && tracked?.canUpdate === false && (
            <div
              className="dlv-delivered-banner"
              role="status"
              style={{
                marginTop: 12,
                padding: "10px 14px",
                borderRadius: 8,
                background: "#fdf1e7",
                color: "#9a5b12",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              ⓘ This consignment is not on a DRS delivery run — no update needed.
            </div>
          )}
          */}
        </section>

        {/* ===== Update consignment ===== */}
        <section className="dlv-card">
          <h2 className="dlv-section-title">Update Consignment</h2>

          <form
            id="dlv-update-form"
            className="dlv-form"
            onSubmit={handleSubmit}
            noValidate
            inert={notUpdatable ? "" : undefined}
          >
            <Field name="dest" label="Delivery Dest." required full error={errors.dest} fieldRef={regField("dest")}>
              <input type="text" placeholder="Enter delivery destination" {...bind("dest")} />
            </Field>

            <Field name="time" label="Time" required error={errors.time} fieldRef={regField("time")}>
              <div className="dlv-time-wrap">
                <input type="date" className="dlv-input" value={todayISO()} readOnly disabled />
                <input
                  type="time"
                  className={`dlv-input ${errors.time ? "invalid" : ""}`}
                  value={timeOnly(form.time)}
                  max={nowTimeOnly()}
                  onChange={(e) => {
                    const picked = e.target.value;
                    const now = nowTimeOnly();
                    // Native time-picker dropdowns don't grey out hours past
                    // `max` — they just fail HTML validation on submit. Snap
                    // any future pick straight back to "now" here so a
                    // forward time can never actually sit in the field.
                    if (picked > now) {
                      notify("Cannot select a future time — set to current time", "bad");
                      setField("time", `${todayISO()}T${now}`);
                    } else {
                      setField("time", `${todayISO()}T${picked}`);
                    }
                  }}
                />
                <button className="dlv-btn dlv-btn-time" type="button" title="Set current time" onClick={() => setField("time", nowLocal())}>
                  <ClockIcon /> Now
                </button>
              </div>
            </Field>

            <Field name="status" label="Status" required error={errors.status} fieldRef={regField("status")}>
              <select id="dlv-status" className={`dlv-input ${errors.status ? "invalid" : ""}`} value={form.status} onChange={handleStatus}>
                <option value="">-Select-</option>
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>

            <Field name="receiver" label="Receiver" required={rules.receiptRequired} error={errors.receiver} fieldRef={regField("receiver")}>
              <input type="text" placeholder="Receiver name" {...bind("receiver")} />
            </Field>

            <Field name="receivedAt" label="Received At" required={rules.receiptRequired} error={errors.receivedAt} fieldRef={regField("receivedAt")}>
              <ReceivedAtAutocomplete
                id="dlv-receivedAt"
                value={form.receivedAt}
                onChange={(v) => setField("receivedAt", v)}
                options={receivedAtOptions}
                placeholder="e.g. Main Gate, Stores"
                invalid={!!errors.receivedAt}
              />
            </Field>

            {rules.showMobile && (
              <Field name="mobile" label="Receiver Mobile" error={errors.mobile} fieldRef={regField("mobile")}>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile"
                  {...bind("mobile")}
                  onChange={(e) => setField("mobile", e.target.value.replace(/\D/g, ""))}
                />
              </Field>
            )}

            {rules.showPkgs && (
              <Field name="pkgs" label="Packages Delivered" required error={errors.pkgs} fieldRef={regField("pkgs")}>
                <input type="number" inputMode="numeric" min="1" placeholder="No. of packages" {...bind("pkgs")} />
              </Field>
            )}

            {rules.showReason && (
              <Field name="reason" label="Reason" required full error={errors.reason} fieldRef={regField("reason")}>
                <select {...bind("reason")}>
                  <option value="">-Select-</option>
                  {reasons.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field name="remarks" label="Remarks" full fieldRef={regField("remarks")}>
              <textarea maxLength={250} placeholder="Any additional remarks" {...bind("remarks")} />
            </Field>

            {rules.showPod && (
              <Field name="pod" label="POD Photo" required={rules.showPod} full error={errors.pod} fieldRef={regField("pod")}>
                <input ref={podInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={handlePod} />
                <div
                  className="dlv-photo-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => podInputRef.current?.click()}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && podInputRef.current?.click()}
                >
                  {pod ? (
                    <>
                      <img src={pod.url} alt="POD preview" />
                      <button
                        type="button"
                        className="dlv-remove"
                        aria-label="Remove photo"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearPod();
                        }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <CameraIcon />
                      <span>Tap to capture / upload proof of delivery</span>
                    </>
                  )}
                </div>
              </Field>
            )}
          </form>
        </section>
      </main>

      <footer className="dlv-footer">
        <div className="dlv-footer-inner">
          <button className="dlv-btn dlv-btn-outline" type="button" onClick={() => resetForm(true)}>
            Clear
          </button>
          <button
            className="dlv-btn dlv-btn-primary"
            type="submit"
            form="dlv-update-form"
            disabled={submitting || notUpdatable}
            title={notUpdatable ? "Already delivered — nothing to update" : undefined}
          >
            {submitting ? (<><span className="dlv-spinner" /> Updating…</>) : "Update Consignment"}
          </button>
        </div>
      </footer>

      {otp.open && (
        <div className="dlv-modal-backdrop" role="dialog" aria-modal="true" aria-label="Enter OTP">
          <div className="dlv-modal">
            <h2 className="dlv-modal-title">Enter OTP</h2>
            <p className="dlv-modal-sub">
              A 4-digit OTP was sent to the consignee{otp.mobile ? ` (${otp.mobile})` : ""}. Ask the consignee for it to confirm delivery.
            </p>
            <input
              className={`dlv-input dlv-otp-input ${otp.error ? "invalid" : ""}`}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              placeholder="• • • •"
              autoFocus
              value={otp.code}
              onChange={(e) => setOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, "").slice(0, 4), error: "" }))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); verifyOtp(); } }}
            />
            {otp.error && <div className="dlv-err">{otp.error}</div>}
            <div className="dlv-modal-actions">
              <button type="button" className="dlv-btn dlv-btn-outline" onClick={closeOtp} disabled={otp.busy}>
                Cancel
              </button>
              <button type="button" className="dlv-btn dlv-btn-primary" onClick={verifyOtp} disabled={otp.busy || otp.code.length !== 4}>
                {otp.busy ? (<><span className="dlv-spinner" /> Please wait…</>) : "Verify & Update"}
              </button>
            </div>
            <button
              type="button"
              className="dlv-link-btn"
              onClick={() => sendOtp(true)}
              disabled={otp.busy || otp.cooldown > 0}
            >
              {otp.cooldown > 0 ? `Resend OTP in ${otp.cooldown}s` : "Resend OTP"}
            </button>
          </div>
        </div>
      )}

      <div className={`dlv-toast ${toast.show ? "show" : ""} ${toast.type}`}>{toast.msg}</div>
    </div>
  );
}
